import { create } from 'zustand'
import { db } from '@/runtime'
import { META } from '@/db/schema'

export type TimeChip = 15 | 30 | 60 | 'any'
export type EnergyChip = 'low' | 'medium' | 'high' | 'any'

export interface Picker {
  time: TimeChip
  energy: EnergyChip
}

interface Flash {
  text: string
  until: number
  /** one button on the flash, e.g. Undo after a delete */
  action?: { label: string; run: () => void }
}

interface UiState {
  activeTaskId: string | null
  picker: Picker
  flash: Flash | null
  quickAddOpen: boolean
  /** text to prefill the next time quick-add opens (share sheet, Shortcuts) */
  quickAddText: string | null
  /** bumps on every open so the sheet remounts with fresh state */
  quickAddSeq: number
  quickAddConcept: boolean
  /** project pages with their own quick-add mounted; the shell's yields to them */
  quickAddScoped: number
  scopeQuickAdd(delta: 1 | -1): void
  /** keyboard cursor: index within the current list */
  cursor: number
  listLength: number
  /** ids of the list the cursor walks (set by the view on screen) */
  listIds: string[]
  /** a globally rendered sheet opened by keyboard */
  menu: { type: 'defer' | 'project'; taskId: string } | null
  switcherOpen: boolean
  paletteOpen: boolean
  sidebarCollapsed: boolean
  /** hide both sidebars and the phone chrome: just the content */
  focusMode: boolean
  /** the desktop right rail a page's panel portals into (null on phones / in focus) */
  rightRailEl: HTMLElement | null
  setRightRail(el: HTMLElement | null): void
  /** sidebar sections folded away, by section key */
  navCollapsed: Record<string, boolean>
  /** folded nodes in the Pages tree, by page id (per device) */
  pagesCollapsed: Record<string, boolean>
  setPagesCollapsed(next: Record<string, boolean>): void
  setListIds(ids: string[]): void
  setMenu(m: UiState['menu']): void
  setSwitcher(open: boolean): void
  setPalette(open: boolean): void
  toggleSidebar(): void
  toggleFocus(): void
  toggleNavSection(key: string): void
  setActiveTask(id: string | null): void
  setPicker(p: Partial<Picker>): void
  /** `action` puts one button on the flash, e.g. Undo after a delete */
  showFlash(text: string, ms?: number, action?: { label: string; run: () => void }): void
  setQuickAdd(open: boolean, text?: string): void
  /** opens the Add sheet with its Concept target on (Learn's own button) */
  openConceptAdd(): void
  takeQuickAddConcept(): boolean
  takeQuickAddText(): string | null
  setCursor(i: number): void
  setListLength(n: number): void
}

const readLocal = (k: string) => {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
const readLocalJson = <T>(k: string, fallback: T): T => {
  try {
    const raw = readLocal(k)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}
const writeLocal = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v)
  } catch {
    /* private mode */
  }
}

export const useUi = create<UiState>((set, get) => ({
  activeTaskId: null,
  picker: { time: 'any', energy: 'any' },
  flash: null,
  quickAddOpen: false,
  quickAddText: null,
  quickAddSeq: 0,
  quickAddConcept: false,
  quickAddScoped: 0,
  scopeQuickAdd(delta) {
    set({ quickAddScoped: Math.max(0, get().quickAddScoped + delta) })
  },
  cursor: 0,
  listLength: 0,
  listIds: [],
  menu: null,
  switcherOpen: false,
  paletteOpen: false,
  sidebarCollapsed: readLocal('sidebar_collapsed') === '1',
  focusMode: readLocal('focus_mode') === '1',
  rightRailEl: null,
  setRightRail(el) {
    if (get().rightRailEl !== el) set({ rightRailEl: el })
  },
  navCollapsed: readLocalJson<Record<string, boolean>>('nav_collapsed', {}),
  pagesCollapsed: readLocalJson<Record<string, boolean>>('pages_collapsed', {}),
  setPagesCollapsed(next) {
    set({ pagesCollapsed: next })
    writeLocal('pages_collapsed', JSON.stringify(next))
  },
  setSwitcher(open) {
    set({ switcherOpen: open })
  },
  setPalette(open) {
    set({ paletteOpen: open })
  },
  toggleSidebar() {
    const collapsed = !get().sidebarCollapsed
    set({ sidebarCollapsed: collapsed })
    writeLocal('sidebar_collapsed', collapsed ? '1' : '0')
  },
  toggleFocus() {
    const focusMode = !get().focusMode
    set({ focusMode })
    writeLocal('focus_mode', focusMode ? '1' : '0')
  },
  toggleNavSection(key) {
    const navCollapsed = { ...get().navCollapsed, [key]: !get().navCollapsed[key] }
    set({ navCollapsed })
    writeLocal('nav_collapsed', JSON.stringify(navCollapsed))
  },
  setListIds(ids) {
    set({ listIds: ids, listLength: ids.length, cursor: Math.min(get().cursor, Math.max(0, ids.length - 1)) })
  },
  setMenu(menu) {
    set({ menu })
  },
  setActiveTask(id) {
    set({ activeTaskId: id })
    void db.setMeta(META.activeTask, id)
  },
  setPicker(p) {
    const picker = { ...get().picker, ...p }
    set({ picker })
    void db.setMeta(META.picker, picker)
  },
  showFlash(text, ms = 4000, action) {
    const until = Date.now() + ms
    set({ flash: { text, until, action } })
    setTimeout(() => {
      if (get().flash?.until === until) set({ flash: null })
    }, ms)
  },
  setQuickAdd(open, text) {
    set({
      quickAddOpen: open,
      ...(open && !get().quickAddOpen ? { quickAddSeq: get().quickAddSeq + 1 } : {}),
      ...(text !== undefined ? { quickAddText: text } : {}),
    })
  },
  openConceptAdd() {
    set({ quickAddConcept: true })
    get().setQuickAdd(true)
  },
  takeQuickAddConcept() {
    const on = get().quickAddConcept
    if (on) set({ quickAddConcept: false })
    return on
  },
  takeQuickAddText() {
    const t = get().quickAddText
    if (t !== null) set({ quickAddText: null })
    return t
  },
  setCursor(i) {
    const n = get().listLength
    set({ cursor: n ? Math.max(0, Math.min(n - 1, i)) : 0 })
  },
  setListLength(n) {
    set({ listLength: n, cursor: Math.min(get().cursor, Math.max(0, n - 1)) })
  },
}))

/** Restore per-device state from Dexie meta on boot. */
export const hydrateUi = async () => {
  const [active, picker] = await Promise.all([
    db.getMeta<string | null>(META.activeTask),
    db.getMeta<Picker>(META.picker),
  ])
  useUi.setState({ activeTaskId: active ?? null, ...(picker ? { picker } : {}) })
}
